/** Save compatibility for the watchtower's wider, physical foundation. */
import { buildingType, buildingTypeIndex } from '../data/buildings.ts';
import { OB_SOLID, TERRAIN_BUILDABLE, Terrain } from './types.ts';
import { evictFromRect, type World } from './world.ts';

type Roof = NonNullable<World['villageBlueprint']>['roofs'][number];
interface TowerMove {
  id: number;
  x: number;
  y: number;
  tiles: number[];
  roof?: Roof;
  rubble: boolean;
}

/**
 * Old slots remain the same slots: workers, attackers and a native siege's
 * damage ledger refer to their indices. Only the plot and occupancy change.
 * No random stream, resources, HP or construction progress are consumed.
 *
 * Ruins need an explicit save marker: four adjacent old one-tile ruins cannot
 * be distinguished from one new four-tile ruin by their type marks alone.
 */
export function migrateWatchtowerFootprints(w: World, legacyRuins: boolean): void {
  const type = buildingTypeIndex('watchtower'), mark = type + 1;
  const b = w.buildings, map = w.map, roofs = w.villageBlueprint?.roofs ?? [];
  const occupied = new Map<number, number[]>();
  for (let i = 0; i < b.slots.high; i++) {
    if (!b.slots.alive[i] || b.typeId[i] !== type) continue;
    const x = b.tx[i], y = b.ty[i];
    if ([0, 1].every(dy => [0, 1].every(dx =>
      map.inBounds(x + dx, y + dy) && map.building[map.idx(x + dx, y + dy)] === i))) continue;
    occupied.set(i, []);
  }
  if (occupied.size === 0 && !legacyRuins) return;
  const ruins: number[] = [];
  for (let k = 0; k < map.building.length; k++) {
    occupied.get(map.building[k])?.push(k);
    if (legacyRuins && map.rubble[k] === mark) ruins.push(k);
  }
  const jobs: TowerMove[] = [];
  const matchingRoof = (x: number, y: number): Roof | undefined =>
    roofs.find(r => r.type === type && r.x === x && r.y === y);
  for (const [id, tiles] of occupied) {
    const x = b.tx[id], y = b.ty[id];
    jobs.push({ id, x, y, tiles, roof: matchingRoof(x, y), rubble: false });
  }
  if (legacyRuins) {
    for (const tile of ruins) {
      const x = tile % map.w, y = Math.floor(tile / map.w);
      jobs.push({ id: -1, x, y, tiles: [tile], roof: matchingRoof(x, y), rubble: true });
    }
    // A village may already have cleared its ruin. It still owes a replacement
    // at the next recovery month, and that saved reservation must fit too.
    for (const roof of roofs) {
      if (roof.type !== type || jobs.some(j => j.roof === roof)) continue;
      const id = map.building[map.idx(roof.x, roof.y)];
      if (id >= 0 && b.slots.alive[id] && b.typeId[id] === type) continue;
      jobs.push({ id: -1, x: roof.x, y: roof.y, tiles: [], roof, rubble: false });
    }
  }
  if (jobs.length === 0) return;

  // Lost preset roofs/walls are also occupied land for this migration. Putting
  // a tower in a breached curtain would obstruct its next automatic repair.
  const reserved = new Uint16Array(map.building.length);
  const roofSize = new Map<Roof, number>();
  const reserve = (x: number, y: number, width: number, height: number, delta: number): void => {
    for (let dy = 0; dy < height; dy++) for (let dx = 0; dx < width; dx++) {
      if (map.inBounds(x + dx, y + dy)) reserved[map.idx(x + dx, y + dy)] += delta;
    }
  };
  for (const roof of roofs) {
    const old = jobs.some(j => j.roof === roof), def = buildingType(roof.type);
    const size = old ? 1 : def.w;
    roofSize.set(roof, size);
    reserve(roof.x, roof.y, size, old ? 1 : def.h, 1);
  }
  for (const wall of w.villageBlueprint?.walls ?? []) reserved[wall.tile]++;

  // Slot order, then saved tile order, is stable across machines and reloads.
  // Each search expands square rings; ties choose the northern/western plot.
  for (const job of jobs) {
    if (job.roof) reserve(job.x, job.y, roofSize.get(job.roof)!, roofSize.get(job.roof)!, -1);
    const own = new Set(job.tiles);
    const free = (x: number, y: number): boolean => {
      for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) {
        const tx = x + dx, ty = y + dy;
        if (!map.inBounds(tx, ty)) return false;
        const k = map.idx(tx, ty), node = w.nodes.at(k);
        if (!TERRAIN_BUILDABLE[map.terrain[k]] || map.wall[k] || reserved[k] ||
            (map.building[k] >= 0 && map.building[k] !== job.id) ||
            (map.rubble[k] !== 0 && !(job.rubble && own.has(k))) ||
            (node >= 0 && w.nodes.amount[node] > 0) || ((w.obstacleGrid?.[k] ?? 0) & OB_SOLID)) return false;
        // Keep existing roads and bridge approaches usable. A legacy tower's
        // own square can already be a road in an old handcrafted settlement.
        if (map.terrain[k] === Terrain.Road && !own.has(k)) return false;
      }
      return true;
    };
    let spot: [number, number] | undefined;
    for (let radius = 0; radius < Math.max(map.w, map.h) && !spot; radius++) {
      for (let y = job.y - radius; y <= job.y + radius && !spot; y++) {
        for (let x = job.x - radius; x <= job.x + radius; x++) {
          if (Math.max(Math.abs(x - job.x), Math.abs(y - job.y)) !== radius) continue;
          if (free(x, y)) { spot = [x, y]; break; }
        }
      }
    }
    if (!spot) throw new Error(`Cannot safely fit the saved watchtower at ${job.x},${job.y} into a 2×2 plot.`);
    const [x, y] = spot;
    const work = job.rubble ? job.tiles.reduce((n, k) => n + map.rubbleWork[k], 0) / 4 : 0;
    const stain = job.rubble ? Math.max(...job.tiles.map(k => map.stain[k])) : 0;
    for (const k of job.tiles) {
      if (job.id >= 0 && map.building[k] === job.id) map.building[k] = -1;
      if (job.rubble) { map.rubble[k] = 0; map.rubbleWork[k] = 0; w.rubbleCount--; }
    }
    if (job.id >= 0) { b.tx[job.id] = x; b.ty[job.id] = y; }
    for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) {
      const k = map.idx(x + dx, y + dy);
      if (job.id >= 0) map.building[k] = job.id;
      if (job.rubble) {
        map.rubble[k] = mark; map.rubbleWork[k] = work;
        map.stain[k] = Math.max(map.stain[k], stain); w.rubbleCount++;
      }
    }
    if (job.roof) { job.roof.x = x; job.roof.y = y; }
    if (job.roof || job.rubble) reserve(x, y, 2, 2, 1);
    if (job.id >= 0) evictFromRect(w, x, y, x + 1, y + 1);
  }
  map.invalidateRubbleIndex();
  map.wallChanged();
  map.terrainVersion++;
  w.dirty.flow = true;
  w.dirty.districts = true;
  w.paths.clear();
}
